import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon, { IconName } from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import AiGeneratedBadge from '../../../../components/ui/AiGeneratedBadge';
import {
  getSkillRoleGaps,
  recommendSkillGaps,
  SkillHoldLevel,
  SkillRequirementSource,
  EquipmentPriority,
  SkillRoleGapsView,
  StrengthAlignmentRowView,
  RequirementStateView,
  SkillGapRecommendationView,
} from '../../../../api/General/businessRoles';

interface SkillRoleGapsSectionProps {
  departmentId?: number;
  onFixEmployee: (employeeId: string) => void;
  /**
   * Reports the result of THIS section's `getSkillRoleGaps` call to the parent so the
   * overview summary card can render the under-covered count without issuing a second,
   * identical request for the same departmentId (CO-205). `null` = the count is unknown
   * (403 for Manager/Chief, or any other failure) and the card must show an em-dash.
   */
  onGapsLoaded?: (underCoveredCount: number | null) => void;
  /** Already debounced by the page. Matches an employee name or any required skill name. */
  searchTerm?: string;
  /** Narrows the table to the rows the API counts as under-covered (coverage below 100%). */
  gapsOnly?: boolean;
}

/**
 * Level presentation. Keyed by the STRING enum member (the API serializes .NET
 * enums as their member name), never by a numeric reverse-map.
 * Every state carries an icon AND a text label so meaning survives without colour.
 */
const LEVEL_PRESENTATION: Record<SkillHoldLevel, { icon: IconName; tone: string; labelKey: string; effectKey: string }> = {
  [SkillHoldLevel.Strong]: {
    icon: 'CheckCircle2',
    tone: 'text-success',
    labelKey: 'skillGaps.level.strong',
    effectKey: 'skillGaps.effect.full',
  },
  [SkillHoldLevel.Working]: {
    icon: 'CircleDot',
    tone: 'text-amber-600',
    labelKey: 'skillGaps.level.working',
    effectKey: 'skillGaps.effect.half',
  },
  [SkillHoldLevel.Awareness]: {
    icon: 'Circle',
    tone: 'text-muted-foreground',
    labelKey: 'skillGaps.level.awareness',
    effectKey: 'skillGaps.effect.quarter',
  },
  [SkillHoldLevel.NotHeld]: {
    icon: 'XCircle',
    tone: 'text-destructive',
    labelKey: 'skillGaps.level.notHeld',
    effectKey: 'skillGaps.effect.zero',
  },
};

const IMPORTANCE_PRESENTATION: Record<EquipmentPriority, { toneClass: string; labelKey: string }> = {
  [EquipmentPriority.Essential]: {
    toneClass: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
    labelKey: 'businessRoles.priority.essential',
  },
  [EquipmentPriority.Important]: {
    toneClass: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    labelKey: 'businessRoles.priority.important',
  },
  [EquipmentPriority.Helpful]: {
    toneClass: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    labelKey: 'businessRoles.priority.helpful',
  },
  [EquipmentPriority.Optional]: {
    toneClass: 'bg-muted text-muted-foreground border-border',
    labelKey: 'businessRoles.priority.optional',
  },
};

const coverageTone = (percent: number): string => {
  if (percent >= 100) return 'text-success';
  if (percent >= 50) return 'text-amber-600';
  return 'text-destructive';
};

const coverageIcon = (percent: number): IconName => {
  if (percent >= 100) return 'CheckCircle2';
  if (percent >= 50) return 'AlertTriangle';
  return 'AlertCircle';
};

const formatNumber = (value: number): string => (Math.round(value * 100) / 100).toString();

const SkillRoleGapsSection: React.FC<SkillRoleGapsSectionProps> = ({
  departmentId,
  onFixEmployee,
  onGapsLoaded,
  searchTerm = '',
  gapsOnly = false,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<SkillRoleGapsView | null>(null);
  const [loading, setLoading] = useState(false);
  // 'forbidden' = the Hr/Admin-only endpoint 403'd (Manager/Chief); 'error' = anything else.
  // Without this, a 403 left data null and the section rendered a heading over a blank page.
  const [outcome, setOutcome] = useState<'ok' | 'forbidden' | 'error'>('ok');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [aiLoading, setAiLoading] = useState<Record<string, boolean>>({});
  const [aiData, setAiData] = useState<Record<string, SkillGapRecommendationView>>({});

  // Held in a ref so a parent passing an inline callback cannot retrigger the fetch effect —
  // the whole point of CO-205 was to REMOVE a duplicate request, not add a re-fetch loop.
  const onGapsLoadedRef = useRef(onGapsLoaded);
  useEffect(() => {
    onGapsLoadedRef.current = onGapsLoaded;
  }, [onGapsLoaded]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await getSkillRoleGaps({ departmentId });
        if (cancelled) return;
        if (result.errors.length === 0 && result.payload) {
          setData(result.payload);
          setOutcome('ok');
          onGapsLoadedRef.current?.(result.payload.underCoveredCount);
        } else {
          setData(null);
          setOutcome(result.status === 403 ? 'forbidden' : 'error');
          onGapsLoadedRef.current?.(null);
        }
      } catch (error) {
        if (cancelled) return;
        // A 403 rejects here with the parsed Result body, whose status is 403.
        const status = (error as { status?: number; response?: { status?: number } })?.status
          ?? (error as { response?: { status?: number } })?.response?.status;
        setData(null);
        setOutcome(status === 403 ? 'forbidden' : 'error');
        onGapsLoadedRef.current?.(null);
        console.error('Failed to load skill role gaps:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [departmentId]);

  const search = searchTerm.trim().toLowerCase();
  const filtersActive = gapsOnly || search.length > 0;

  // The header counts stay on the API totals: they describe the whole set, not the filtered slice.
  const visibleRows = useMemo(() => {
    let rows = data?.rows ?? [];
    if (gapsOnly) rows = rows.filter(row => row.coveragePercent < 100);
    if (search) {
      rows = rows.filter(row =>
        (row.employeeName ?? row.employeeId).toLowerCase().includes(search)
        || row.requirements.some(requirement => requirement.skillName?.toLowerCase().includes(search)));
    }
    return rows;
  }, [data, gapsOnly, search]);

  const toggleRow = useCallback((employeeId: string) => {
    setExpanded(prev => ({ ...prev, [employeeId]: !prev[employeeId] }));
  }, []);

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, employeeId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleRow(employeeId);
    }
  };

  const handleGenerate = async (event: React.MouseEvent, employeeId: string) => {
    event.stopPropagation();
    setAiLoading(prev => ({ ...prev, [employeeId]: true }));
    try {
      const result = await recommendSkillGaps(employeeId);
      if (result.errors.length === 0 && result.payload) {
        setAiData(prev => ({ ...prev, [employeeId]: result.payload }));
      }
    } catch (error) {
      console.error('Failed to generate skill gap recommendations:', error);
    } finally {
      setAiLoading(prev => ({ ...prev, [employeeId]: false }));
    }
  };

  /*
   * Where the requirement came from, as an undertitle beneath the skill name.
   * `sourceDetail` carries the role name for Role and the goal title for Goal. For Both the server
   * sends only the strictest one and does not say which kind it is, so that case names neither
   * rather than risk labelling a goal as a role.
   */
  const sourceLine = (requirement: RequirementStateView): string | null => {
    const name = requirement.sourceDetail?.trim();
    if (requirement.source === SkillRequirementSource.Both) return t('skillGaps.source.both');
    if (!name) return null;
    if (requirement.source === SkillRequirementSource.Role) return t('skillGaps.source.role', { name });
    if (requirement.source === SkillRequirementSource.Goal) return t('skillGaps.source.goal', { name });
    return null;
  };

  const renderRequirement = (row: StrengthAlignmentRowView, requirement: RequirementStateView) => {
    const level = LEVEL_PRESENTATION[requirement.level];
    const importance = IMPORTANCE_PRESENTATION[requirement.importance];
    const displayName = row.employeeName ?? row.employeeId;

    return (
      <li
        key={requirement.skillId}
        data-testid={`gap-req-${row.employeeId}-${requirement.skillId}`}
        /* A fixed trailing column, not `ms-auto`: only a grid keeps every contribution value on the
           same edge once the skill line wraps. */
        className="grid grid-cols-1 items-start gap-x-6 gap-y-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_9rem]"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Icon name={level.icon} size={16} className={`shrink-0 ${level.tone}`} />
            <span className="text-sm font-semibold text-foreground">{requirement.skillName}</span>
            {importance && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${importance.toneClass}`}>
                {t(importance.labelKey)}
              </span>
            )}
          </div>
          {sourceLine(requirement) && (
            <p
              className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"
              data-testid={`gap-req-source-${row.employeeId}-${requirement.skillId}`}
            >
              <Icon
                name={requirement.source === SkillRequirementSource.Role ? 'Briefcase' : 'Target'}
                size={12}
                className="shrink-0"
              />
              {sourceLine(requirement)}
            </p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            <span className={level.tone}>{t(level.labelKey, { name: displayName })}</span>{' '}
            {t(level.effectKey)}
          </p>
        </div>
        <span
          data-testid={`gap-contrib-${row.employeeId}-${requirement.skillId}`}
          className="text-xs tabular-nums text-muted-foreground sm:text-end"
        >
          {t('skillGaps.contribution', {
            contribution: formatNumber(requirement.contribution),
            weight: formatNumber(requirement.weight),
          })}
        </span>
      </li>
    );
  };

  const renderAiPanel = (row: StrengthAlignmentRowView) => {
    const recommendation = aiData[row.employeeId];
    const isLoading = Boolean(aiLoading[row.employeeId]);

    return (
      <div data-testid={`gap-ai-${row.employeeId}`} className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            {t('skillGaps.ai.heading')}
            {/* One provenance marker across the product, rather than a Sparkles glyph here, a badge
                in the coverage panel and a footer line on the dashboard. */}
            <AiGeneratedBadge visible={Boolean(recommendation?.aiAvailable)} />
          </h3>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Sparkles"
              loading={isLoading}
              data-testid={`gap-ai-btn-${row.employeeId}`}
              onClick={(event: React.MouseEvent) => handleGenerate(event, row.employeeId)}
            >
              {isLoading
                ? t('skillGaps.ai.loading')
                : recommendation
                  ? t('skillGaps.ai.refresh')
                  : t('skillGaps.ai.generate')}
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Wrench"
              data-testid={`gap-fix-${row.employeeId}`}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                onFixEmployee(row.employeeId);
              }}
            >
              {t('skillGaps.fix')}
            </Button>
          </div>
        </div>

        {recommendation && (
          <div data-testid={`gap-ai-panel-${row.employeeId}`} className="space-y-3">
            <p className="text-sm text-foreground">{recommendation.summary}</p>
            <ul className="space-y-2">
              {recommendation.recommendations.map((item, index) => {
                const importance = IMPORTANCE_PRESENTATION[item.importance];
                return (
                  <li
                    key={`${item.skillName}-${index}`}
                    data-testid={`gap-ai-rec-${row.employeeId}-${index}`}
                    className="rounded-md border border-border p-3"
                  >
                    {/* Action-led: the diagnosis (skill, importance, held level) already
                        lives in the requirements list above — here we lead with what to do. */}
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        {item.action}
                      </span>
                      <span className="text-sm font-medium text-foreground">{item.skillName}</span>
                      {importance && <span className="text-xs text-muted-foreground">· {t(importance.labelKey)}</span>}
                    </div>
                    {item.detail && <p className="text-xs text-muted-foreground">{item.detail}</p>}
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-muted-foreground flex items-start">
              <Icon name="Info" size={12} className="me-1 mt-0.5 shrink-0" />
              {recommendation.aiAvailable ? t('skillGaps.ai.disclaimer') : t('skillGaps.ai.disclaimerFallback')}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderExpandedRow = (row: StrengthAlignmentRowView) => (
    <tr key={`${row.employeeId}-detail`} className="bg-muted/40">
      <td colSpan={6} className="px-4 py-4">
        <div className="space-y-4">
          <div data-testid={`gap-requirements-${row.employeeId}`}>
            <h3 className="text-sm font-semibold text-foreground mb-2">{t('skillGaps.requirementsHeading')}</h3>
            <ul>{row.requirements.map(requirement => renderRequirement(row, requirement))}</ul>
          </div>

          {row.unusedStrengths.length > 0 && (
            <div data-testid={`gap-unused-${row.employeeId}`}>
              <h3 className="text-sm font-semibold text-foreground mb-2">{t('skillGaps.unusedHeading')}</h3>
              <div className="flex flex-wrap gap-2 mb-1">
                {row.unusedStrengths.map(strength => (
                  <span key={strength} className="px-2 py-1 rounded-full text-xs border border-border bg-muted text-muted-foreground">
                    {strength}
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{t('skillGaps.unusedHint')}</p>
            </div>
          )}

          {row.unresolved.length > 0 && (
            <div
              data-testid={`gap-unresolved-${row.employeeId}`}
              className="rounded-lg border border-amber-200 bg-amber-50 p-3"
            >
              <h3 className="text-sm font-semibold text-amber-900 mb-1 flex items-center">
                <Icon name="AlertTriangle" size={16} className="me-2 text-amber-600" />
                {t('skillGaps.unresolvedHeading')}
              </h3>
              <ul className="list-disc ps-5 space-y-0.5 mb-1">
                {row.unresolved.map((item, index) => (
                  <li key={`${item.rawName}-${index}`} className="text-xs text-amber-900">
                    {t('skillGaps.unresolvedItem', { name: item.rawName, role: item.roleName ?? '' })}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-amber-800">{t('skillGaps.unresolvedHint')}</p>
            </div>
          )}

          {renderAiPanel(row)}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Icon name="AlertTriangle" size={24} className="me-2 text-orange-600" />
            {t('skillGaps.heading')}
          </h2>
          <p className="text-sm text-gray-600 mt-1">{t('skillGaps.subheading')}</p>
        </div>
        {data && data.totalWithRequirements > 0 && (
          <div className="text-end">
            <span data-testid="gaps-under-count" className="text-sm text-gray-600 flex items-center">
              <Icon name="AlertCircle" size={16} className="me-1 text-orange-500" />
              {t('skillGaps.underCovered', { count: data.underCoveredCount, total: data.totalWithRequirements })}
            </span>
            {filtersActive && (
              <p data-testid="gaps-showing-count" className="mt-1 text-xs text-muted-foreground">
                {t('skillGaps.showingRows', { shown: visibleRows.length, total: data.rows.length })}
              </p>
            )}
          </div>
        )}
      </div>

      {loading && (
        <div role="status" aria-label={t('common.loading')} className="flex items-center justify-center py-10">
          <Icon name="Loader2" size={24} className="animate-spin text-primary" />
        </div>
      )}

      {!loading && outcome === 'forbidden' && (
        <div data-testid="gaps-forbidden" className="rounded-lg border border-dashed border-border p-8 text-center">
          <Icon name="Info" size={28} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-600">{t('skillGaps.forbidden')}</p>
        </div>
      )}

      {!loading && outcome === 'error' && (
        <div data-testid="gaps-error" className="rounded-lg border border-dashed border-border p-8 text-center">
          <Icon name="AlertCircle" size={28} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-600">{t('skillGaps.loadError')}</p>
        </div>
      )}

      {!loading && data && data.totalWithRequirements === 0 && (
        <div data-testid="gaps-empty" className="rounded-lg border border-dashed border-border p-8 text-center">
          <Icon name="Inbox" size={28} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-600">{t('skillGaps.empty')}</p>
        </div>
      )}

      {!loading && data && data.totalWithRequirements > 0 && visibleRows.length === 0 && (
        <div data-testid="gaps-no-matches" className="rounded-lg border border-dashed border-border p-8 text-center">
          <Icon name="SearchX" size={28} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {gapsOnly && !search ? t('skillGaps.noGapsMatch') : t('skillGaps.noMatches')}
          </p>
        </div>
      )}

      {!loading && data && visibleRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="w-8 px-2 py-2" />
                <th scope="col" className="px-3 py-2 text-start font-medium text-gray-600">
                  {t('skillGaps.colEmployee')}
                </th>
                <th scope="col" className="px-3 py-2 text-start font-medium text-gray-600">
                  {t('skillGaps.colDepartment')}
                </th>
                <th scope="col" className="px-3 py-2 text-start font-medium text-gray-600">
                  {t('skillGaps.colRoles')}
                </th>
                <th scope="col" title={t('skillGaps.colCoverageTooltip')} className="px-3 py-2 text-end font-medium text-gray-600">
                  {t('skillGaps.colCoverage')}
                </th>
                <th
                  scope="col"
                  title={t('skillGaps.colUtilizationTooltip')}
                  className="px-3 py-2 text-end font-medium text-gray-600"
                >
                  {t('skillGaps.colUtilization')}
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Rows arrive sorted worst-first from the API — do not re-sort. */}
              {visibleRows.map(row => {
                const isOpen = Boolean(expanded[row.employeeId]);
                const coverage = Math.round(row.coveragePercent);
                return (
                  <React.Fragment key={row.employeeId}>
                    <tr
                      data-testid={`gap-row-${row.employeeId}`}
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onClick={() => toggleRow(row.employeeId)}
                      onKeyDown={event => handleRowKeyDown(event, row.employeeId)}
                      className="border-b border-border cursor-pointer hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <td className="px-2 py-3">
                        <Icon name={isOpen ? 'ChevronDown' : 'ChevronRight'} size={16} className="text-gray-500 rtl:-scale-x-100" />
                      </td>
                      <td className="px-3 py-3 font-medium text-gray-900">{row.employeeName ?? row.employeeId}</td>
                      <td className="px-3 py-3 text-gray-600">{row.department ?? '-'}</td>
                      <td className="px-3 py-3 text-gray-600">{row.roleNames ?? '-'}</td>
                      <td data-testid={`gap-coverage-${row.employeeId}`} className="px-3 py-3 text-end">
                        <span className={`inline-flex items-center justify-end font-medium ${coverageTone(coverage)}`}>
                          <Icon name={coverageIcon(coverage)} size={14} className="me-1" />
                          {coverage}%
                        </span>
                      </td>
                      <td data-testid={`gap-utilization-${row.employeeId}`} className="px-3 py-3 text-end text-gray-600">
                        {row.utilizationPercent == null ? '-' : `${Math.round(row.utilizationPercent)}%`}
                      </td>
                    </tr>
                    {isOpen && renderExpandedRow(row)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SkillRoleGapsSection;
